import { useId, useMemo } from 'react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import type { ChartConfig } from '@/components/ui/chart'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import type {
  PerformanceMetric,
  PerformancePoint,
} from '@/lib/performance.types'
import {
  formatMetricPercent,
  formatPerformanceTimestamp,
  METRIC_LABEL,
  performanceMetricValue,
} from '@/lib/performanceFormat'
import { useTrackerNavigate } from '@/provider/useTrackerNavigate'
import type {
  ChartDatum,
  DotRenderProps,
  PerformanceMetricChartProps,
  PointDotProps,
  PointSummaryLine,
  TooltipContentProps,
} from './PerformanceMetricChart.types'
import { useRovingPerformancePoint } from './useRovingPerformancePoint'

const METRIC_COLOR: Record<PerformanceMetric, string> = {
  visibilityRate: 'hsl(var(--chart-1))',
  weightedScorePercent: 'hsl(var(--chart-2))',
  shareOfVoice: 'hsl(var(--chart-3))',
}

const HIT_RADIUS = 12

// Overrides ChartContainer's default aspect-video: Expanded stacks three of these; Focused is one, bigger.
const SIZE_CLASS: Record<PerformanceMetricChartProps['size'], string> = {
  expanded: 'aspect-auto h-[240px] sm:h-[280px]',
  focused: 'aspect-auto h-[300px] sm:h-[420px]',
}

function sourceLabel(point: PerformancePoint): string {
  return point.source === 'scheduled' ? 'Scheduled' : 'Manual'
}

function searchLabel(point: PerformancePoint): string {
  return point.liveSearch ? 'Live Search' : 'Standard'
}

// The tooltip and the aria-live summary both render off this list, so they can never drift apart.
function pointSummaryLines(point: PerformancePoint): PointSummaryLine[] {
  return [
    {
      label: 'Measured',
      value: formatPerformanceTimestamp(point.measurementAt),
    },
    { label: 'Source', value: sourceLabel(point) },
    { label: 'Search', value: searchLabel(point) },
    {
      label: 'Visibility Rate',
      value: formatMetricPercent(point, 'visibilityRate'),
    },
    {
      label: 'Weighted Score',
      value: formatMetricPercent(point, 'weightedScorePercent'),
    },
    {
      label: 'Share of Voice',
      value: formatMetricPercent(point, 'shareOfVoice'),
    },
  ]
}

function tickFormatter(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function PerformanceChartTooltip({ active, payload }: TooltipContentProps) {
  const point = payload?.[0]?.payload?.point
  if (!active || !point) return null

  return (
    <div className="grid gap-1 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      {pointSummaryLines(point).map((line) => (
        <div key={line.label} className="flex justify-between gap-3">
          <span className="text-muted-foreground">{line.label}</span>
          <span className="font-medium tabular-nums text-foreground">
            {line.value}
          </span>
        </div>
      ))}
    </div>
  )
}

// A null point never gets a dot, active or otherwise: no rendered mark, no clickable target.
function PointDot({
  cx,
  cy,
  payload,
  radius,
  onHover,
  onSelect,
}: PointDotProps) {
  if (
    cx === undefined ||
    cy === undefined ||
    !payload ||
    payload.value === null
  ) {
    return null
  }

  const runId = payload.point.runId

  return (
    <g>
      {/* Invisible hit target, wider than the visible dot so mouse users get an affordance comparable to the keyboard's roving tabstop. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: pointer-only enhancement; the chart wrapper's roving tabstop already covers this for keyboard users. */}
      <circle
        cx={cx}
        cy={cy}
        r={HIT_RADIUS}
        className="fill-transparent cursor-pointer"
        onMouseEnter={() => onHover(runId)}
        onClick={() => onSelect(runId)}
      />
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        className="fill-primary stroke-background stroke-2 pointer-events-none"
      />
    </g>
  )
}

export function PerformanceMetricChart({
  points,
  metric,
  size,
}: PerformanceMetricChartProps) {
  const navigate = useTrackerNavigate()
  const summaryId = useId()
  const label = METRIC_LABEL[metric]
  const color = METRIC_COLOR[metric]

  function activate(runId: string) {
    navigate(`/run/${runId}`)
  }

  const roving = useRovingPerformancePoint(points, metric, activate)

  const chartData = useMemo<ChartDatum[]>(
    () =>
      points.map((point) => ({
        measurementAt: point.measurementAt,
        value: performanceMetricValue(point, metric),
        point,
      })),
    [points, metric]
  )

  const chartConfig = useMemo<ChartConfig>(
    () => ({ [metric]: { label, color } }),
    [metric, label, color]
  )

  const nonNullCount = chartData.filter((datum) => datum.value !== null).length
  const firstPoint = points[0]
  const lastPoint = points.at(-1)

  const seriesAlternative =
    firstPoint && lastPoint
      ? `${label} across ${points.length} measurements from ${formatPerformanceTimestamp(firstPoint.measurementAt)} to ${formatPerformanceTimestamp(lastPoint.measurementAt)}, ${nonNullCount} with a value.`
      : `${label}: no measurements in this range.`

  return (
    <div className="space-y-2">
      {/* biome-ignore lint/a11y/useSemanticElements: a <fieldset> is a form control grouping, not a chart; role="group" is the correct ARIA fit here. */}
      <div
        tabIndex={0}
        role="group"
        aria-label={`${label} trend chart. Use the arrow keys to move between points, Home and End for the ends, Enter to open the highlighted run.`}
        aria-describedby={summaryId}
        onKeyDown={roving.onKeyDown}
        className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <p className="sr-only">{seriesAlternative}</p>
        <ChartContainer config={chartConfig} className={SIZE_CLASS[size]}>
          <LineChart data={chartData} accessibilityLayer={false}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="measurementAt"
              tickFormatter={tickFormatter}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <YAxis
              domain={[0, 100]}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <ChartTooltip content={<PerformanceChartTooltip />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              connectNulls={false}
              isAnimationActive={false}
              dot={(dotProps: DotRenderProps) => (
                <PointDot
                  {...dotProps}
                  radius={3}
                  onHover={roving.setActiveRunId}
                  onSelect={activate}
                />
              )}
              activeDot={(dotProps: DotRenderProps) => (
                <PointDot
                  {...dotProps}
                  radius={5}
                  onHover={roving.setActiveRunId}
                  onSelect={activate}
                />
              )}
            />
          </LineChart>
        </ChartContainer>
      </div>

      <p
        id={summaryId}
        aria-live="polite"
        className="min-h-[1rem] text-xs text-muted-foreground space-x-2"
      >
        {roving.activePoint ? (
          pointSummaryLines(roving.activePoint).map((line) => (
            <span key={line.label}>
              {line.label}: {line.value}
            </span>
          ))
        ) : (
          <span>No point selected.</span>
        )}
      </p>
    </div>
  )
}
